import { useCallback } from 'react';
import { observer } from 'mobx-react';
import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { useLocalization } from '@ui-framework/index';
import {
  ButtonContainer,
  IconLibrary,
  PermissionButton,
} from '@product-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';

const PERMISSION_SP = 'SERVICEPROVIDERNOTIFICATION';

function InsightContactDrawer({
  formId,
  formRef,
  title,
  open,
  onClose,
  onSubmit,
  schema,
  validationSchema,
  defaultValues,
  fetchDefaultValues,
}) {
  const { formatMessage } = useLocalization();

  const showUnsavedChangesDialog = useCallback(() => {
    const unSavedDialog = formRef.current.getActionDialog();

    unSavedDialog.init({
      message: formatMessage({
        id: 'INSIGHT_CONTACT_UNSAVED_CHANGES_BODY',
        defaultMessage:
          'You have unsaved changes for this phone number. Close without saving?',
      }),
      width: 360,
      size: 'small',
      buttons: [
        {
          label: formatMessage({
            id: 'INSIGHT_CONTACT_UNSAVED_CHANGES_SAVE_AND_PROCEED',
            defaultMessage: 'Save and Proceed',
          }),
          onClick: () => {
            formRef.current?.submit({
              formId,
              onSuccess: () => {
                unSavedDialog.close();
                onClose();
              },
              onFailure: () => {
                unSavedDialog.close();
              },
            });
          },
        },
        {
          label: formatMessage({
            id: 'INSIGHT_CONTACT_UNSAVED_CHANGES_DISCARD_AND_PROCEED',
            defaultMessage: 'Discard and Proceed',
          }),
          onClick: () => {
            formRef.current?.reset();
            unSavedDialog.close();
            onClose();
          },
        },
        {
          label: formatMessage({
            id: 'INSIGHT_CONTACT_UNSAVED_CHANGES_CLOSE',
            defaultMessage: 'Close',
          }),
          onClick: () => {
            unSavedDialog.close();
          },
          variant: 'outlined',
        },
      ],
    });

    unSavedDialog.open();
  }, [formRef, formId, onClose]);

  const handleClose = useCallback(() => {
    if (formRef.current?.isDirty()) {
      showUnsavedChangesDialog();
    } else {
      onClose();
    }
  }, [onClose, showUnsavedChangesDialog]);

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box
        sx={{
          width: { xs: '100vw', sm: 440 },
          maxWidth: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <IconLibrary.Phone />
            <Typography variant="h6">{title}</Typography>
          </Stack>
          <IconButton
            aria-label={formatMessage({
              id: 'CLOSE',
              defaultMessage: 'Close',
            })}
            onClick={handleClose}
            size="small">
            <IconLibrary.Close />
          </IconButton>
        </Stack>

        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            px: 2,
            py: 2,
            minHeight: 320,
            width: '100%',
          }}>
          {open && (
            <Form
              formId={formId}
              ref={formRef}
              permissionEntities={[PERMISSION_SP]}
              onSubmit={onSubmit}
              schema={schema}
              validationSchema={validationSchema}
              {...(defaultValues && { defaultValues })}
              {...(fetchDefaultValues && { fetchDefaultValues })}>
              <ButtonContainer
                sx={{
                  p: 0,
                  mt: 2,
                  mb: 0,
                  pt: 2,
                }}>
                <PermissionButton
                  permissionName="CAN_UPDATE_SP_INSIGHT_CONTACTS"
                  variant="contained"
                  color="primary"
                  startIcon={<IconLibrary.Save />}
                  onClick={() => formRef.current?.submit({ formId })}>
                  {formatMessage({
                    id: 'INSIGHT_CONFIG_SAVE_BUTTON',
                    defaultMessage: 'Save',
                  })}
                </PermissionButton>
                <PermissionButton
                  permissionName="CAN_UPDATE_SP_INSIGHT_CONTACTS"
                  variant="contained"
                  color="primary"
                  startIcon={<IconLibrary.Reset />}
                  onClick={() => formRef.current?.reset()}>
                  {formatMessage({
                    id: 'INSIGHT_CONFIG_RESET_BUTTON',
                    defaultMessage: 'Reset',
                  })}
                </PermissionButton>
              </ButtonContainer>
            </Form>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}

export default observer(InsightContactDrawer);
